import * as React from 'react';
import {
  HealthProfilePageWrapper,
  MoreOptionsCard,
  MoreOptionsLink,
} from '@leagueplatform/health-profile-common';
import {
  StackLayout,
  Button,
  HeadingText,
  UtilityText,
  Box,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  LoadingIndicator,
  Sidebar,
} from '@leagueplatform/web-common-components';
import {
  DATE_FORMAT,
  useFormatDateByUserProfileLocation,
} from '@leagueplatform/web-common';
import { track } from '@leagueplatform/analytics';
import { useInfiniteHealthMetricsV2 } from '#hooks/use-get-health-metrics-v2.hook.ts';
import { ListItem } from '#components/list-item/index.ts';
import { ErrorPanel } from '#components/error-panel/index.ts';
import { getValueUnitDisplayLabel } from '#utils/get-value-unit-display-label.ts';

import { HealthPrograms } from '#pages/details2/components/health-programs.tsx';
import { HealthLiteracyContent } from '#components/health-literacy/health-literacy-content.tsx';
import { useIsSelfReportedDataEnabled } from '#hooks/use-is-self-reported-data-enabled.hook.ts';
import { AddMetricDataButton } from '#pages/details2/components/add-metric-data-button.tsx';
import { useHealthMetricDocumentTitle } from '#hooks/use-health-metric-document-title.ts';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';

type Params = {
  dataType: string;
};
const handleAnalytics = (metricType?: string) =>
  track('dashboard.screen_loaded.all_recorded_data_metric_type', {
    metric_type: metricType || '',
    metric_id: '',
    metric_status: '',
  });

export const AllRecordedData = () => {
  const { dataType } = useParams<Params>();
  const { formatMessage } = useIntl();
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();
  const { isSelfReportedDataEnabled } = useIsSelfReportedDataEnabled();
  const {
    isLoading: isLoadingHealthMetrics,
    isError: isErrorHealthMetrics,
    isRefetching: isRefetchingHealthMetrics,
    data: healthMetrics,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    hasNextPage,
    error,
  } = useInfiniteHealthMetricsV2({
    filter: {
      type: dataType,
    },
    fields: { type: ['type', 'value', 'source', 'unit', 'timestamp'] },
  });

  const metricDisplayName = healthMetrics?.pages[0].data[0].metricDisplayName;

  useHealthMetricDocumentTitle(
    formatMessage({
      id: 'ALL_RECORDED_DATA',
    }),
    metricDisplayName,
  );

  React.useEffect(() => {
    handleAnalytics(dataType);
  }, [dataType]);

  if (isLoadingHealthMetrics && !healthMetrics) {
    return <LoadingIndicator />;
  }
  const allMetricConfigs = healthMetrics?.pages[0].data[0].configs.data;
  const firstConfigName = allMetricConfigs?.[0].name;
  // display the config name with the entry if there are multiple metric configs
  // and if the `name` field of the metric configs are different
  const displayConfigName =
    Boolean(allMetricConfigs?.length) &&
    !allMetricConfigs?.every(
      (metricConfig) => metricConfig.name === firstConfigName,
    );
  // TODO: implement handleEditClick functionality after completion of
  // edit data screens in https://everlong.atlassian.net/browse/ABTM-4654
  const handleEditClick = () => {
    handleAnalytics(dataType);
  };

  const showHealthLiteracyOnAllRecordedDataScreen =
    healthMetrics?.pages[0].data[0].showHealthLiteracyOnAllRecordedDataScreen;
  const showAddDataButton =
    healthMetrics?.pages[0].data[0].addDataUrl && isSelfReportedDataEnabled;
  return (
    <HealthProfilePageWrapper
      title={formatMessage({
        id: 'ALL_RECORDED_DATA',
      })}
      sidebar={
        <Sidebar>
          <MoreOptionsCard>
            <MoreOptionsLink
              to="data-sources"
              onClick={() =>
                track(
                  'dashboard.button_clicked.metric_item_metric_type.navigate_to_metric_tab',
                  {
                    metric_type: dataType,
                    metric_id: '',
                    metric_status: '',
                    content_id: '',
                    contentful_system: '',
                    detail: 'data sources',
                  },
                )
              }
            >
              {formatMessage({ id: 'VIEW_DATA_SOURCES' })}
            </MoreOptionsLink>
          </MoreOptionsCard>
          {showAddDataButton ? (
            <AddMetricDataButton
              onClick={() =>
                track(
                  'dashboard.button_clicked.metric_item_metric_type.add_data',
                  {
                    metric_type: dataType,
                    metric_id: '',
                    is_empty_state: '',
                    content_id: '',
                    contentful_system: '',
                  },
                )
              }
            />
          ) : null}
        </Sidebar>
      }
    >
      <HeadingText
        size="xxl"
        level="2"
        css={{ paddingTop: '$oneAndHalf', paddingBottom: '$twoAndHalf' }}
      >
        {metricDisplayName}
      </HeadingText>
      <StackLayout spacing="$oneAndHalf">
        {isErrorHealthMetrics ? (
          <ErrorPanel
            error={error}
            isRefetching={isRefetchingHealthMetrics}
            onRetry={refetch}
          />
        ) : (
          <Box as="ul" css={{ width: '100%' }}>
            {healthMetrics?.pages.map((page) =>
              page.data[0].values.data.map((dataMetric) => {
                const { id, source, timestamp, config } = dataMetric;

                const isSelfReported = config.data.source === 'self_reported';
                const date = formatDateWithUserProfileLocation(
                  timestamp,
                  DATE_FORMAT,
                );

                return (
                  <ListItem as="li" key={id}>
                    <StackLayout>
                      <UtilityText as="p" emphasis="emphasized">
                        {getValueUnitDisplayLabel({
                          value: dataMetric.value,
                          label: dataMetric.label,
                          unitOptions: dataMetric.config.data.unit,
                        }).map(([displayValue, displayUnit], index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <span key={`${index} + ${dataMetric.type}`}>
                            {displayValue} {displayUnit}{' '}
                            {displayConfigName
                              ? ` (${config.data.name.toLocaleLowerCase()})`
                              : null}
                          </span>
                        ))}
                      </UtilityText>
                      <UtilityText emphasis="subtle">{source}</UtilityText>
                    </StackLayout>
                    <StackLayout
                      orientation="horizontal"
                      horizontalAlignment="end"
                      verticalAlignment="center"
                      spacing="$half"
                    >
                      <UtilityText
                        as="p"
                        emphasis="subtle"
                        css={{ paddingTop: '$half', paddingBottom: '$half' }}
                      >
                        {date}
                      </UtilityText>
                      {isSelfReported && isSelfReportedDataEnabled && (
                        <Button
                          onClick={handleEditClick}
                          priority="secondary"
                          quiet
                          icon="descriptiveCompose"
                          hideLabel
                        >
                          {formatMessage(
                            { id: 'EDIT_METRIC_DATA' },
                            {
                              metric: metricDisplayName,
                            },
                          )}
                        </Button>
                      )}
                    </StackLayout>
                  </ListItem>
                );
              }),
            )}
            {hasNextPage && (
              <StackLayout
                horizontalAlignment="center"
                css={{ marginTop: '$twoAndHalf' }}
              >
                <Button
                  priority="secondary"
                  disabled={isFetchingNextPage}
                  onClick={() => {
                    fetchNextPage();
                  }}
                >
                  {formatMessage({ id: 'LOAD_MORE' }).toLocaleUpperCase()}
                </Button>
              </StackLayout>
            )}
          </Box>
        )}
        {showHealthLiteracyOnAllRecordedDataScreen ? (
          <>
            <HealthPrograms metric={dataType} />
            <HealthLiteracyContent metric={dataType} />
          </>
        ) : null}
      </StackLayout>
    </HealthProfilePageWrapper>
  );
};
